import type { MessageLevel, TaskEvent, TaskId, Unit } from "./events.ts";

/**
 * The producing side: what code that is doing something long says about
 * itself. A producer is given the task to report into, so the caller chooses
 * the label and ends the task.
 */
export interface Reporter {
    task(label: string, options?: TaskOptions): Task;
    message(level: MessageLevel, text: string): void;
}

export interface TaskOptions {
    total?: number;
    unit?: Unit;
}

export interface ProgressOptions {
    /** A total that is only known, or is revised, while the task runs. */
    total?: number;
    /** What the task is working on now, for a renderer to show beside it. */
    detail?: string;
}

/** A task reports into itself and is the parent of the tasks it starts. */
export interface Task extends Reporter {
    readonly id: TaskId;
    progress(done: number, options?: ProgressOptions): void;
    done(): void;
    failed(error: string): void;
}

/**
 * A reporter that emits events. `source` identifies this reporter in the task ids,
 * so that streams from several threads can be merged into one view.
 */
export function createReporter(emit: (event: TaskEvent) => void, source: string): Reporter {
    let nextId = 1;

    function reporterFor(parent: TaskId | undefined): Reporter {
        return {
            task(label: string, options?: TaskOptions): Task {
                const id = `${source}/${nextId++}`;
                emit({ kind: "start", id, parent, label, ...options });
                const own = reporterFor(id);
                return {
                    id,
                    task: own.task,
                    message: own.message,
                    progress: (done, progressOptions) => emit({ kind: "progress", id, done, ...progressOptions }),
                    done: () => emit({ kind: "end", id }),
                    failed: (error) => emit({ kind: "end", id, error }),
                };
            },
            message(level: MessageLevel, text: string): void {
                emit({ kind: "message", level, text, task: parent });
            },
        };
    }

    return reporterFor(undefined);
}

/**
 * Coalesce progress events to at most one per task per interval. Everything
 * else passes through, after the progress waiting behind it, so that a task's
 * last position is never posted after its end.
 *
 * A producer that reports per network chunk or per compiled file otherwise
 * costs one message per chunk on the thread rendering it.
 */
export function throttle(emit: (event: TaskEvent) => void, intervalMs: number): (event: TaskEvent) => void {
    const waiting = new Map<TaskId, TaskEvent>();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const flush = (): void => {
        if (timer !== undefined) clearTimeout(timer);
        timer = undefined;
        const events = [...waiting.values()];
        waiting.clear();
        for (const event of events) emit(event);
    };

    return (event: TaskEvent): void => {
        if (event.kind === "progress") {
            waiting.set(event.id, event);
            if (timer === undefined) timer = unrefed(setTimeout(flush, intervalMs));
            return;
        }
        flush();
        emit(event);
    };
}

/** A pending progress update must not be what keeps a Node process alive. */
export function unrefed<T>(timer: T): T {
    (timer as { unref?: () => void }).unref?.();
    return timer;
}
